import { useState, useEffect, useRef } from 'react'
import type { ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Typography,
  Stack,
  Container,
  Card,
  CardContent,
  Box,
  Grid,
  Button,
  Table,
  Sheet,
  Chip,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Select as _Select,
  Option as _Option,
  Avatar,
  Textarea as _Textarea,
  Tooltip,
  CircularProgress,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
} from '@mui/joy'
import {
  Contacts as ContactsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Block as BlockIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  CheckCircle as VerifiedIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import api from '../services/api'
import { socketService } from '../services/socket'
import { useAuth } from '../hooks/useAuth'
import { UserRole } from '../utils/permissions'
import translateBackendError from '../utils/translateBackendError'
import { contactAvatarInitial, displayContactName } from '../utils/contactDisplay'

interface WhatsappLite {
  id: number
  name: string
}

interface Contact {
  id: number
  name: string
  number: string
  email?: string
  profilePicUrl?: string
  companyId: number
  extraInfo?: any[]
  tags?: any[]
  wallets?: any[]
  isGroup?: boolean
  active?: boolean
  acceptAudioMessage?: boolean
  disableBot?: boolean
  createdAt: string
  remoteJid?: string | null
  whatsappId?: number | null
  whatsapp?: WhatsappLite | null
  // Estado de verificación de WhatsApp: null=sin verificar | pending | valid | invalid
  whatsappValid?: string | null
}

const isTechnicalLidContact = (contact: Contact): boolean => {
  const number = String(contact.number || '').trim()
  const remoteJid = contact.remoteJid || ''
  const isTechnicalJid = Boolean(
    remoteJid &&
    !remoteJid.endsWith('@s.whatsapp.net') &&
    !remoteJid.endsWith('@g.us')
  )

  return Boolean(
    isTechnicalJid &&
    contact.name === contact.number &&
    /^[0-9]+$/.test(number)
  )
}

const contactNameForTable = (contact: Contact): string => {
  if (isTechnicalLidContact(contact)) return 'Sin nombre'
  return displayContactName(contact)
}

const contactNumberForTable = (contact: Contact): string => {
  if (isTechnicalLidContact(contact)) return '-'
  return contact.number || '-'
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Badge del estado de verificación de WhatsApp del contacto.
const WHATSAPP_BADGE: Record<
  string,
  { label: string; color: 'success' | 'danger' | 'warning' }
> = {
  valid: { label: 'Verificado', color: 'success' },
  invalid: { label: 'Sin WhatsApp', color: 'danger' },
  pending: { label: 'Verificando…', color: 'warning' },
}

function renderWhatsappBadge(contact: Contact) {
  const state = contact.whatsappValid || ''
  const cfg = WHATSAPP_BADGE[state]
  if (!cfg) return null
  const icon =
    state === 'valid' ? (
      <VerifiedIcon sx={{ fontSize: 14 }} />
    ) : state === 'invalid' ? (
      <BlockIcon sx={{ fontSize: 14 }} />
    ) : (
      <PendingIcon sx={{ fontSize: 14 }} />
    )
  return (
    <Tooltip title={`WhatsApp: ${cfg.label}`} size="sm">
      <Chip size="sm" variant="soft" color={cfg.color} startDecorator={icon}>
        {cfg.label}
      </Chip>
    </Tooltip>
  )
}

export default function Contacts() {
  const navigate = useNavigate()
  const { user } = useAuth()

  // Obtener rol del usuario
  const userRole: UserRole = (user?.profile?.toLowerCase()?.includes('super') ? 'super' :
    user?.profile?.toLowerCase()?.includes('admin') ? 'admin' :
    user?.profile?.toLowerCase()?.includes('supervisor') ? 'supervisor' : 'user') as UserRole

  // Permisos de acciones sobre contactos
  const canEditContacts = userRole === 'super' || userRole === 'admin' || userRole === 'supervisor'
  const canDeleteContacts = userRole === 'super' || userRole === 'admin'
  const canBlockContacts = userRole === 'super' || userRole === 'admin' || userRole === 'supervisor'
  const canCreateContacts = userRole === 'super' || userRole === 'admin' || userRole === 'supervisor'

  const [contacts, setContacts] = useState<Contact[]>([])
  const [whatsapps, setWhatsapps] = useState<WhatsappLite[]>([])
  const [whatsappFilter, setWhatsappFilter] = useState<number | ''>('')
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [openModal, setOpenModal] = useState(false)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [openingChatId, setOpeningChatId] = useState<number | null>(null)
  // Selector cuando un contacto tiene conversación en varias conexiones.
  const [contactoParaElegir, setContactoParaElegir] = useState<Contact | null>(null)
  const [ticketsDelContacto, setTicketsDelContacto] = useState<any[]>([])
  const [importing, setImporting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [formData, setFormData] = useState<{
    name: string
    number: string
    email: string
  }>({
    name: '',
    number: '',
    email: '',
  })
  const [formError, setFormError] = useState<string | null>(null)
  const importInputRef = useRef<HTMLInputElement | null>(null)

  // Debounce de búsqueda para no saturar el backend
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPageNumber(1) // Resetear a página 1 al buscar
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Resetear a página 1 cuando cambia el filtro por conexión
  useEffect(() => {
    setPageNumber(1)
  }, [whatsappFilter])

  // Cargar lista de conexiones WhatsApp para el filtro
  useEffect(() => {
    const fetchWhatsapps = async () => {
      try {
        const response = await api.get('/whatsapp/')
        const data: WhatsappLite[] = Array.isArray(response.data)
          ? response.data.map((w: any) => ({ id: Number(w.id), name: String(w.name ?? `#${w.id}`) }))
          : []
        setWhatsapps(data)
      } catch (error) {
        console.error('Error fetching whatsapps:', error)
        setWhatsapps([])
      }
    }
    fetchWhatsapps()
  }, [])

  useEffect(() => {
    fetchContacts()
  }, [pageNumber, debouncedSearch, rowsPerPage, whatsappFilter])

  const fetchContacts = async () => {
    try {
      setLoading(true)
      const params: Record<string, string | number> = {
        pageNumber,
        rowsPerPage,
      }
      if (debouncedSearch) {
        params.searchParam = debouncedSearch
      }
      if (whatsappFilter !== '') {
        params.whatsappId = whatsappFilter
      }
      const response = await api.get('/contacts', { params })

      // Backend returns { contacts, count, hasMore }
      const contactsData = response.data.contacts || response.data || []
      setContacts(contactsData)
      setTotalCount(response.data.count || 0)
      setHasMore(response.data.hasMore || false)
    } catch (error) {
      console.error('Error fetching contacts:', error)
      setContacts([])
      setTotalCount(0)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    setFormError(null)
    if (!formData.name?.trim()) {
      setFormError('El nombre es obligatorio')
      return
    }
    if (!formData.number?.trim()) {
      setFormError('El teléfono es obligatorio')
      return
    }
    try {
      await api.post('/contacts', formData)
      fetchContacts()
      setOpenModal(false)
      resetForm()
    } catch (error: unknown) {
      setFormError(translateBackendError(error, 'Error al crear el contacto'))
      console.error('Error creating contact:', error)
    }
  }

  const handleUpdate = async () => {
    if (!selectedContact) return
    try {
      await api.put(`/contacts/${selectedContact.id}`, formData)
      fetchContacts()
      setOpenModal(false)
      resetForm()
    } catch (error) {
      setFormError(translateBackendError(error, 'Error al actualizar el contacto'))
      console.error('Error updating contact:', error)
    }
  }

  const handleDelete = async (contactId: number) => {
    if (confirm('¿Estás seguro de eliminar este contacto?')) {
      try {
        await api.delete(`/contacts/${contactId}`)
        toast.success('Contacto eliminado')
        fetchContacts()
      } catch (error) {
        toast.error(translateBackendError(error, 'Error al eliminar el contacto'))
        console.error('Error deleting contact:', error)
      }
    }
  }

  const handleBlockUnblock = async (contact: Contact) => {
    const nextActive = !contact.active
    try {
      await api.put(`/contacts/block/${contact.id}`, { active: nextActive })
      toast.success(nextActive ? 'Contacto desbloqueado' : 'Contacto bloqueado')
      fetchContacts()
    } catch (error) {
      toast.error(translateBackendError(error, 'Error al bloquear/desbloquear el contacto'))
      console.error('Error blocking/unblocking contact:', error)
    }
  }

  const _handleToggleBot = async (contactId: number) => {
    try {
      await api.put(`/contacts/toggleDisableBot/${contactId}`)
      fetchContacts()
    } catch (error) {
      console.error('Error toggling bot:', error)
    }
  }

  const _handleToggleAudio = async (contactId: number) => {
    try {
      await api.put(`/contacts/toggleAcceptAudio/${contactId}`)
      fetchContacts()
    } catch (error) {
      console.error('Error toggling audio:', error)
    }
  }

  // Refresca el badge de verificación de WhatsApp en tiempo real (job en background).
  useEffect(() => {
    if (!user?.companyId) return
    const socket = socketService.getSocket()
    if (!socket) return
    const channel = `company-${user.companyId}-contact`
    const handler = (data: any) => {
      // Solo nos interesa el estado de verificación; y solo si viene en el payload,
      // para no pisar el badge cuando otro emisor manda 'update' sin whatsappValid.
      if (data?.action === 'update' && data.contact?.id && data.contact.whatsappValid !== undefined) {
        setContacts(prev =>
          prev.map(c =>
            c.id === data.contact.id
              ? { ...c, whatsappValid: data.contact.whatsappValid }
              : c
          )
        )
      }
    }
    socket.on(channel, handler)
    return () => { socket.off(channel, handler) }
  }, [user?.companyId])

  const handleDownloadTemplate = () => {
    // Plantilla CSV compatible con la importación (cabeceras: nombre, numero, email).
    // Excel abre el CSV y el import (ImportContactsService) reconoce estas columnas.
    const headers = 'nombre,numero,email'
    const example1 = 'Juan Perez,593991234567,juan@ejemplo.com'
    const example2 = 'Maria Lopez,593987654321,'
    const csv = `${headers}\n${example1}\n${example2}\n`
    // BOM para que Excel detecte UTF-8 (acentos / ñ).
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    downloadBlob(blob, 'plantilla-contactos.csv')
  }

  const handleImportContacts = async () => {
    importInputRef.current?.click()
  }

  const handleImportFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)

    try {
      setImporting(true)
      const { data } = await api.post('/contacts/upload', formData)
      const created = data?.createdCount ?? 0
      const duplicated = data?.duplicated ?? 0
      const skipped = data?.skippedInvalid ?? 0

      if (created > 0) {
        const extra: string[] = []
        if (duplicated > 0) extra.push(`${duplicated} ya existían`)
        if (skipped > 0) extra.push(`${skipped} inválidos omitidos`)
        toast.success(
          `Importación completada: ${created} nuevos` +
          (extra.length ? ` (${extra.join(', ')})` : '') +
          '. Verificando WhatsApp en segundo plano…'
        )
      } else {
        const reason =
          skipped > 0 ? `${skipped} filas inválidas` :
          duplicated > 0 ? 'todos los contactos ya existían' : 'sin contactos nuevos'
        toast.info(`Importación finalizada: ${reason}.`)
      }
      fetchContacts()
    } catch (error) {
      toast.error(translateBackendError(error, 'Error al importar contactos'))
      console.error('Error importing contacts:', error)
    } finally {
      setImporting(false)
    }
  }

  const handleExportContacts = async () => {
    try {
      setExporting(true)
      // Respeta el filtro de conexión activo en la vista: si hay una conexión
      // seleccionada, se exportan solo sus contactos; si es "Todas", se exporta todo.
      const response = await api.post('/contacts/export/excel', {
        whatsappId: whatsappFilter || undefined,
      })
      const jobId = response.data?.jobId
      const filename = response.data?.filename || `contactos-${Date.now()}.xlsx`

      if (!jobId) {
        throw new Error('No se recibió jobId de exportación')
      }

      const selectedConn = whatsappFilter
        ? whatsapps.find((w) => w.id === whatsappFilter)?.name
        : null
      toast.info(
        selectedConn
          ? `Exportando contactos de "${selectedConn}". Preparando el archivo...`
          : 'Exportación iniciada. Preparando el archivo...'
      )

      for (let attempt = 0; attempt < 18; attempt += 1) {
        try {
          const downloadResponse = await api.get(`/contacts/export/download/${jobId}`, {
            params: { filename },
            responseType: 'blob',
            timeout: 60000,
          })
          downloadBlob(downloadResponse.data, filename)
          toast.success('Exportación descargada')
          return
        } catch (downloadError) {
          if (attempt === 17) throw downloadError
          await sleep(5000)
        }
      }
    } catch (error) {
      toast.error(translateBackendError(error, 'Error al exportar contactos'))
      console.error('Error exporting contacts:', error)
    } finally {
      setExporting(false)
    }
  }

  /**
   * Abre la conversación del contacto.
   *
   * Antes hacía POST /tickets y navegaba con `contactId`, dejando que la
   * pantalla de Tickets adivinara cuál era. Eso fallaba de forma intermitente:
   * un contacto puede tener ticket en VARIAS conexiones, y el backend solo
   * buscaba en la conexión "por defecto" — creando un ticket nuevo vacío o
   * abriendo el chat de otra persona.
   *
   * Ahora se pregunta primero qué tickets existen:
   *   0  → se crea uno y se abre
   *   1  → se abre directamente
   *   2+ → el usuario elige cuál (no se adivina)
   */
  const handleOpenChat = async (contact: Contact) => {
    try {
      setOpeningChatId(contact.id)

      const { data: existentes } = await api.get(
        `/tickets/by-contact/${contact.id}`
      )

      if (Array.isArray(existentes) && existentes.length > 1) {
        setTicketsDelContacto(existentes)
        setContactoParaElegir(contact)
        return
      }

      if (Array.isArray(existentes) && existentes.length === 1) {
        navigate('/tickets', { state: { ticketId: existentes[0].id } })
        return
      }

      // Sin ticket activo: se crea. El backend devuelve el ticket (y si ya
      // existía uno, lo devuelve con alreadyOpen) — se usa su id en vez de
      // descartarlo como se hacía antes.
      const { data: ticket } = await api.post('/tickets', {
        contactId: contact.id,
        status: 'open',
        userId: user?.id,
      })

      navigate('/tickets', {
        state: ticket?.id ? { ticketId: ticket.id } : { contactId: contact.id },
      })
    } catch (error: any) {
      console.error('Error opening chat:', error)
      // Última red: navegar por contacto para no dejar al usuario atascado.
      navigate('/tickets', { state: { contactId: contact.id } })
    } finally {
      setOpeningChatId(null)
    }
  }

  /** Abre el ticket elegido en el selector y cierra el modal. */
  const abrirTicketElegido = (ticketId: number) => {
    setContactoParaElegir(null)
    setTicketsDelContacto([])
    navigate('/tickets', { state: { ticketId } })
  }

  const openEditModal = (contact: Contact) => {
    setSelectedContact(contact)
    setFormError(null)
    setFormData({
      name: contact.name,
      number: contact.number,
      email: contact.email || '',
    })
    setOpenModal(true)
  }

  const openCreateModal = () => {
    setSelectedContact(null)
    resetForm()
    setOpenModal(true)
  }

  const resetForm = () => {
    setFormError(null)
    setFormData({
      name: '',
      number: '',
      email: '',
    })
  }

  const totalPages = Math.ceil(totalCount / rowsPerPage)

  const stats = {
    total: totalCount,
    individuals: contacts.filter((c) => !c.isGroup).length,
    groups: contacts.filter((c) => c.isGroup).length,
    botDisabled: contacts.filter((c) => c.disableBot).length,
  }

  return (
    <Container maxWidth="xl">
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <ContactsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography level="h2">Contactos</Typography>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Gestión de contactos y clientes
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            {canCreateContacts && (
              <>
                <Dropdown>
                  <MenuButton
                    startDecorator={<UploadIcon />}
                    variant="outlined"
                    color="neutral"
                    loading={importing}
                    disabled={importing || exporting}
                  >
                    Importar
                  </MenuButton>
                  <Menu placement="bottom-start" sx={{ minWidth: 200 }}>
                    <MenuItem onClick={handleImportContacts}>
                      <UploadIcon />
                      Subir archivo
                    </MenuItem>
                    <MenuItem onClick={handleDownloadTemplate}>
                      <DownloadIcon />
                      Descargar plantilla
                    </MenuItem>
                  </Menu>
                </Dropdown>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={handleImportFileChange}
                />
                <Button
                  startDecorator={<DownloadIcon />}
                  variant="outlined"
                  color="neutral"
                  onClick={handleExportContacts}
                  loading={exporting}
                  disabled={importing || exporting}
                >
                  Exportar
                </Button>
              </>
            )}
            <IconButton variant="outlined" color="neutral" onClick={fetchContacts}>
              <RefreshIcon />
            </IconButton>
            {canCreateContacts && (
              <Button startDecorator={<AddIcon />} color="primary" onClick={openCreateModal}>
                Nuevo Contacto
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Stats */}
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Total Contactos
                </Typography>
                <Typography level="h2">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Individuales
                </Typography>
                <Typography level="h2" sx={{ color: 'primary.main' }}>
                  {stats.individuals}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          {/* Card "Grupos" comentada (a pedido)
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Grupos
                </Typography>
                <Typography level="h2" sx={{ color: 'success.main' }}>
                  {stats.groups}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          */}
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Bot Deshabilitado
                </Typography>
                <Typography level="h2">{stats.botDisabled}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search */}
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Input
                placeholder="Buscar contactos por nombre, teléfono o email..."
                startDecorator={<SearchIcon />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flex: 1 }}
              />
              <_Select
                value={whatsappFilter === '' ? '' : String(whatsappFilter)}
                onChange={(_e, value) => {
                  if (value === '' || value === null || value === undefined) {
                    setWhatsappFilter('')
                  } else {
                    setWhatsappFilter(Number(value))
                  }
                }}
                placeholder="Todas las conexiones"
                startDecorator={<WhatsAppIcon />}
                sx={{ minWidth: 240 }}
              >
                <_Option value="">Todas las conexiones</_Option>
                {whatsapps.map((w) => (
                  <_Option key={w.id} value={String(w.id)}>
                    {w.name}
                  </_Option>
                ))}
              </_Select>
            </Stack>
          </CardContent>
        </Card>

        {/* Contacts Table */}
        <Card>
          <Sheet sx={{ overflow: 'auto' }}>
            <Table stickyHeader>
              <thead>
                <tr>
                  <th style={{ width: 60 }}></th>
                  <th style={{ width: 200 }}>Nombre</th>
                  <th style={{ width: 150 }}>Teléfono</th>
                  <th style={{ width: 200 }}>Email</th>
                  <th style={{ width: 140 }}>Origen</th>
                  <th style={{ width: 100 }}>Tipo</th>
                  <th>Tags</th>
                  <th style={{ width: 100 }}>Audio</th>
                  <th style={{ width: 80 }}>Bot</th>
                  <th style={{ width: 180 }}>Fecha Creación</th>
                  <th style={{ width: 180 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={11} style={{ textAlign: 'center', padding: '2rem' }}>
                      <Typography>Cargando contactos...</Typography>
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan={11} style={{ textAlign: 'center', padding: '2rem' }}>
                      <Typography>
                        {whatsappFilter !== ''
                          ? 'No hay contactos en esta conexión'
                          : 'No se encontraron contactos'}
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  contacts.map((contact) => (
                    <tr key={contact.id}>
                      <td>
                        <Avatar size="sm" src={contact.profilePicUrl}>
                          {isTechnicalLidContact(contact) ? '?' : contactAvatarInitial(contact)}
                        </Avatar>
                      </td>
                      <td>
                        <Typography level="body-sm" fontWeight="bold">
                          {contactNameForTable(contact)}
                        </Typography>
                      </td>
                      <td>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography level="body-xs">{contactNumberForTable(contact)}</Typography>
                          {renderWhatsappBadge(contact)}
                        </Stack>
                      </td>
                      <td>
                        <Typography level="body-xs">{contact.email || '-'}</Typography>
                      </td>
                      <td>
                        {contact.whatsapp?.name ? (
                          <Chip size="sm" variant="soft" color="neutral">
                            {contact.whatsapp.name}
                          </Chip>
                        ) : (
                          <Chip size="sm" variant="soft" color="primary">
                            Global
                          </Chip>
                        )}
                      </td>
                      <td>
                        <Chip size="sm" color={contact.isGroup ? 'success' : 'primary'}>
                          {contact.isGroup ? 'Grupo' : 'Individual'}
                        </Chip>
                      </td>
                      <td>
                        <Stack direction="row" spacing={0.5}>
                          {contact.tags?.map((tag: any, index) => (
                            <Chip key={index} size="sm" variant="soft">
                              {typeof tag === 'string' ? tag : tag.name}
                            </Chip>
                          ))}
                        </Stack>
                      </td>
                      <td>
                        <Chip
                          size="sm"
                          color={contact.acceptAudioMessage ? 'success' : 'neutral'}
                          variant="soft"
                        >
                          {contact.acceptAudioMessage ? 'Sí' : 'No'}
                        </Chip>
                      </td>
                      <td>
                        <Chip
                          size="sm"
                          color={contact.disableBot ? 'danger' : 'success'}
                          variant="soft"
                        >
                          {contact.disableBot ? 'Off' : 'On'}
                        </Chip>
                      </td>
                      <td>
                        <Typography level="body-xs">
                          {contact.createdAt && !isNaN(new Date(contact.createdAt).getTime())
                            ? new Date(contact.createdAt).toLocaleDateString('es-ES')
                            : '-'}
                        </Typography>
                      </td>
                      <td>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Abrir conversacion">
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="success"
                              onClick={() => handleOpenChat(contact)}
                              disabled={openingChatId === contact.id}
                            >
                              {openingChatId === contact.id
                                ? <CircularProgress size="sm" />
                                : <WhatsAppIcon />}
                            </IconButton>
                          </Tooltip>
                          {canEditContacts && (
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="primary"
                              onClick={() => openEditModal(contact)}
                              title="Editar"
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {/* Botón "Bloquear" comentado (a pedido)
                          {canBlockContacts && (
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="warning"
                              onClick={() => handleBlockUnblock(contact)}
                              title={contact.active === false ? 'Desbloquear' : 'Bloquear'}
                            >
                              <BlockIcon />
                            </IconButton>
                          )}
                          */}
                          {canDeleteContacts && (
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="danger"
                              onClick={() => handleDelete(contact.id)}
                              title="Eliminar"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Stack>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Sheet>
        </Card>

        {/* Paginación */}
        <Card>
          <CardContent>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              {/* Info de registros */}
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Mostrando {contacts.length === 0 ? 0 : (pageNumber - 1) * rowsPerPage + 1}
                {' - '}
                {Math.min(pageNumber * rowsPerPage, totalCount)} de{' '}
                <strong>{totalCount.toLocaleString()}</strong> contactos
              </Typography>

              {/* Controles de paginación */}
              <Stack direction="row" spacing={1} alignItems="center">
                {/* Selector de filas por página */}
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  Filas:
                </Typography>
                <_Select
                  size="sm"
                  value={rowsPerPage}
                  onChange={(_e, val) => {
                    if (val) {
                      setRowsPerPage(val)
                      setPageNumber(1)
                    }
                  }}
                  sx={{ minWidth: 70 }}
                >
                  <_Option value={10}>10</_Option>
                  <_Option value={20}>20</_Option>
                  <_Option value={50}>50</_Option>
                  <_Option value={100}>100</_Option>
                </_Select>

                {/* Botones de navegación */}
                <IconButton
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber(1)}
                  title="Primera página"
                >
                  <FirstPageIcon />
                </IconButton>
                <IconButton
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  title="Página anterior"
                >
                  <ChevronLeftIcon />
                </IconButton>

                <Typography level="body-sm" sx={{ px: 1 }}>
                  Página <strong>{pageNumber}</strong> de <strong>{totalPages || 1}</strong>
                </Typography>

                <IconButton
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  disabled={!hasMore}
                  onClick={() => setPageNumber((p) => p + 1)}
                  title="Página siguiente"
                >
                  <ChevronRightIcon />
                </IconButton>
                <IconButton
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  disabled={!hasMore}
                  onClick={() => setPageNumber(totalPages)}
                  title="Última página"
                >
                  <LastPageIcon />
                </IconButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Modal Create/Edit */}
        <Modal open={openModal} onClose={() => { setOpenModal(false); setFormError(null) }}>
          <ModalDialog sx={{ minWidth: { xs: 0, sm: 'min(500px, calc(100vw - 32px))' } }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              {selectedContact ? 'Editar Contacto' : 'Nuevo Contacto'}
            </Typography>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Nombre</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre del contacto"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Teléfono</FormLabel>
                <Input
                  value={formData.number}
                  onChange={(e) => setFormData({ ...formData, number: e.target.value.replace(/\+/g, '') })}
                  placeholder="593987654321"
                  startDecorator={<PhoneIcon />}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Email (Opcional)</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@ejemplo.com"
                  startDecorator={<EmailIcon />}
                />
              </FormControl>
              {formError && (
                <Typography
                  level="body-sm"
                  sx={{
                    color: 'danger.600',
                    bgcolor: 'danger.softBg',
                    px: 1.5,
                    py: 1,
                    borderRadius: 'sm',
                  }}
                >
                  {formError}
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  p: 2,
                  bgcolor: 'background.level1',
                  borderRadius: 'sm',
                }}
              >
                <Typography level="body-sm" sx={{ flex: 1 }}>
                  <strong>Vista Previa:</strong>
                  <br />
                  <strong>{formData.name || 'Nombre del contacto'}</strong>
                  <br />
                  {formData.number || 'Teléfono'}
                  {formData.email && (
                    <>
                      <br />
                      {formData.email}
                    </>
                  )}
                </Typography>
              </Box>
              <Button color="primary" onClick={selectedContact ? handleUpdate : handleCreate}>
                {selectedContact ? 'Actualizar' : 'Crear'} Contacto
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>
      </Stack>
      {/* Selector de conversación: solo aparece si el contacto tiene ticket
          activo en más de una conexión. Antes el sistema elegía por su cuenta
          y acababa abriendo el chat equivocado. */}
      <Modal
        open={Boolean(contactoParaElegir)}
        onClose={() => setContactoParaElegir(null)}
      >
        <ModalDialog sx={{ maxWidth: 520, width: '100%' }}>
          <ModalClose />
          <Typography level="title-lg">Elige la conversación</Typography>
          <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 2 }}>
            {contactoParaElegir?.name} tiene {ticketsDelContacto.length}{' '}
            conversaciones abiertas en conexiones distintas.
          </Typography>

          <Stack spacing={1}>
            {ticketsDelContacto.map((t: any) => (
              <Sheet
                key={t.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 'sm',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'background.level1' },
                }}
                onClick={() => abrirTicketElegido(t.id)}
              >
                <Stack
                  direction="row"
                  sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="title-sm">{t.connectionName}</Typography>
                    <Typography
                      level="body-xs"
                      sx={{ color: 'text.tertiary' }}
                      noWrap
                    >
                      {t.lastMessage || 'Sin mensajes'}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Ticket #{t.id}
                      {t.userName ? ` · ${t.userName}` : ' · sin asignar'}
                      {t.queueName ? ` · ${t.queueName}` : ''}
                    </Typography>
                  </Box>
                  <Stack spacing={0.5} alignItems="flex-end">
                    <Chip size="sm" color={t.status === 'open' ? 'success' : 'warning'}>
                      {t.status}
                    </Chip>
                    {t.unreadMessages > 0 && (
                      <Chip size="sm" color="danger">
                        {t.unreadMessages} sin leer
                      </Chip>
                    )}
                  </Stack>
                </Stack>
              </Sheet>
            ))}
          </Stack>

          <Button
            variant="plain"
            onClick={() => setContactoParaElegir(null)}
            sx={{ mt: 2, alignSelf: 'flex-end' }}
          >
            Cancelar
          </Button>
        </ModalDialog>
      </Modal>

    </Container>
  )
}
